import toast from "react-hot-toast";
import { BeatLoader } from "react-spinners";
import Chat from "./neighborhood/Chat";
import News from "./neighborhood/News";
import { FaChevronDown } from "react-icons/fa";
import { Button, Drawer } from "@material-tailwind/react";
import { MdLocationOn } from "react-icons/md";
import { ChevronLeft, Menu, MapPin } from "lucide-react";
import Pinboard from "./neighborhood/Pinboard";
import { useDispatch, useSelector } from "react-redux";
import PinboardPopup from "./neighborhood/PinboardPopup";
import SliderMenu from "./neighborhood/SliderMenu";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { IoIosArrowDown, IoIosArrowForward, IoMdClose } from "react-icons/io";
import { accountBlockUser, AccountProfile } from "../redux/ApiSlice/AccountProfileSlice";
import { userReportNews } from "../redux/ApiSlice/Neighborhood/NeighborhoodNewsReportSlice";
import { getReportReasons } from "../redux/ApiSlice/ReportSlice";
import { getNeighborhoodNews, neighborhoodNewsFilter } from "../redux/ApiSlice/Neighborhood/NeighborhoodNewsSlice";
import { getSessionLocation, postLocation } from "../redux/ApiSlice/LocationSlice";

const getBrowser = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Edg/")) return "edge";
    if (ua.includes("Chrome")) return "chrome";
    if (ua.includes("Firefox")) return "firefox";
    if (ua.includes("Safari")) return "safari";
    return "other";
};

const BROWSER_STEPS = {
    chrome: [
        'Click the 🔒 lock icon in the address bar',
        'Select "Site settings"',
        'Under Permissions, find "Location"',
        'Change it from "Block" to "Allow"',
    ],
    edge: [
        'Click the 🔒 lock icon in the address bar',
        'Click "Permissions for this site"',
        'Find "Location" and set to "Allow"',
    ],
    firefox: [
        'Click the 🔒 lock icon in the address bar',
        'Click ">" next to "Connection secure"',
        'Click "More information" → "Permissions" tab',
        'Find "Access Your Location" → select "Allow"',
    ],
    safari: [
        'Open Safari menu → "Settings for This Website"',
        'Find "Location" and change to "Allow"',
    ],
    other: [
        'Click the lock or info icon in the address bar',
        'Go to site permissions or settings',
        'Find "Location" and set to "Allow"',
    ],
};


const FILTERS = ["Most Recent", "This Week", "This Month", "YTD", "All"];

const FILTER_API_MAP = {
    "This Week": "this_week",
    "This Month": "this_month",
    "YTD": "ytd",
    "All": "all",
};

const getApiFilter = (f) => FILTER_API_MAP[f] || f;

const Neighborhood = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const { neighborhoodNews, loading: newsLoading, isLoadMore, next: newsNext } = useSelector((state) => state.NeighborhoodNews);
    const reportNewsLoading = useSelector((state) => state.NeighborhoodReportNews.loading);
    const { reportReasons = [], reportReasonsLoading } = useSelector((state) => state.Report);
    const { sessionLocation, permissionType } = useSelector((state) => state.LocationAddress);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedPin, setSelectedPin] = useState(null);
    const [showLocationPermissionDialog, setShowLocationPermissionDialog] = useState(false);
    const [browserPermission, setBrowserPermission] = useState("prompt");
    const [isDetecting, setIsDetecting] = useState(false);
    const permissionStatusRef = useRef(null);

    const [activeTab, setActiveTab] = useState(() => "chat");
    const [filter, setFilter] = useState("Most Recent");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [page, setPage] = useState(1);
    const [fetchKey, setFetchKey] = useState(0);
    const newsScrollRef = useRef(null);
    const [blockUserId, setBlockUserId] = useState(null);
    const [blockUserInfo, setBlockUserInfo] = useState(null);
    const [reportNews, setReportNews] = useState(null);
    const [selectedNewsId, setSelectedNewsId] = useState(null);
    const [reportNewsText, setReportNewsText] = useState("");
    const [reportReason, setReportReason] = useState(null);
    const [reportStep, setReportStep] = useState("reason");
    const [reportNewsSuccess, setReportNewsSuccess] = useState(false);
    const [selectedSubReason, setSelectedSubReason] = useState("");
    const [reportError, setReportError] = useState("");
    const [detectBlockedError, setDetectBlockedError] = useState(false);

    const isFetchingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const lastPageRef = useRef(0);
    const loadingRef = useRef(false);
    const isLoadMoreRef = useRef(false);

    useEffect(() => { hasMoreRef.current = newsNext !== null; }, [newsNext]);
    useEffect(() => { loadingRef.current = newsLoading; }, [newsLoading]);
    useEffect(() => { isLoadMoreRef.current = isLoadMore; }, [isLoadMore]);

    useEffect(() => {
        setPage(1);
        isFetchingRef.current = false;
        hasMoreRef.current = true;
        lastPageRef.current = 0;
        dispatch({ type: "NeighborhoodNews/resetNews" });
        if (!User_profile?.id) dispatch(AccountProfile());
        dispatch(getSessionLocation());
    }, [dispatch]);

    useEffect(() => {
        if (!navigator.permissions) return;
        navigator.permissions.query({ name: "geolocation" }).then((status) => {
            setBrowserPermission(status.state);
            permissionStatusRef.current = status;
            status.onchange = () => setBrowserPermission(status.state);
        });
        return () => {
            if (permissionStatusRef.current) permissionStatusRef.current.onchange = null;
        };
    }, []);

    useEffect(() => {
        if (permissionType === "denied") {
            setDetectBlockedError(false);
            setShowLocationPermissionDialog(true);
        }
    }, [permissionType]);

    const uploadLocationForPermission = useCallback(async (lat, lng, permissionTypeValue) => {
        try {
            const response = await dispatch(postLocation({ latitude: lat, longitude: lng, permission_type: permissionTypeValue }));
            if (response?.payload?.message) {
                toast.success(response.payload.message);
                dispatch(getSessionLocation());
                dispatch(getNeighborhoodNews({ page: 1 }));
                setShowLocationPermissionDialog(false);
            }
        } catch (error) {
            toast.error("Error saving location");
        } finally {
            setIsDetecting(false);
        }
    }, [dispatch]);

    useEffect(() => {
        if (!showLocationPermissionDialog || browserPermission !== "granted") return;
        const t = setTimeout(() => {
            setShowLocationPermissionDialog(false);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    uploadLocationForPermission(position.coords.latitude, position.coords.longitude, "while_using");
                },
                () => toast.error("Unable to get location. Please try again.")
            );
        }, 600);
        return () => clearTimeout(t);
    }, [browserPermission, showLocationPermissionDialog, uploadLocationForPermission]);

    const handleLocationPermissionChoice = (choice) => {
        setShowLocationPermissionDialog(false);
        if (choice === "deny") {
            setIsDetecting(false);
            return;
        }
        if (!navigator.geolocation) {
            toast.error("Geolocation is not supported by this browser.");
            return;
        }
        setIsDetecting(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                uploadLocationForPermission(position.coords.latitude, position.coords.longitude, choice);
            },
            () => {
                toast.error("Location is blocked in your browser. Enable it in site settings to use location features.");
                setIsDetecting(false);
                setShowLocationPermissionDialog(true);
            }
        );
    };

    const handleDetectWhenBlocked = () => {
        if (!navigator.geolocation) return;
        setIsDetecting(true);
        setDetectBlockedError(false);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                uploadLocationForPermission(position.coords.latitude, position.coords.longitude, "while_using");
            },
            () => {
                setIsDetecting(false);
                setDetectBlockedError(true);
            }
        );
    };

    const handleGetNeighborhoodNews = useCallback(async (pageNum) => {
        if (isFetchingRef.current) return;
        if (!hasMoreRef.current && pageNum > 1) return;
        isFetchingRef.current = true;
        try {
            const response = await dispatch(getNeighborhoodNews({ page: pageNum })).unwrap();
            hasMoreRef.current = response.next !== null;
            lastPageRef.current = pageNum;
        } catch (error) {
            toast.error(error?.message || "Failed to fetch neighborhood news. Please try again later.");
        } finally {
            isFetchingRef.current = false;
        }
    }, [dispatch]);

    const handleNewsScroll = useCallback(() => {
        const div = newsScrollRef.current;
        if (!div) return;
        const scrollTop = div.scrollTop;
        const scrollHeight = div.scrollHeight;
        const clientHeight = div.clientHeight;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        if (isAtBottom && hasMoreRef.current && !loadingRef.current && !isLoadMoreRef.current && !isFetchingRef.current) {
            setPage((prev) => {
                if (prev === lastPageRef.current) return prev + 1;
                return prev;
            });
        }
    }, []);

    useEffect(() => {
        const div = newsScrollRef.current;
        if (div) div.addEventListener("scroll", handleNewsScroll);
        return () => { if (div) div.removeEventListener("scroll", handleNewsScroll); };
    }, [handleNewsScroll]);

    useEffect(() => {
        const isNextSequentialPage = page === lastPageRef.current + 1;
        const shouldFetch = !isFetchingRef.current && (hasMoreRef.current || page === 1) && isNextSequentialPage;
        if (!shouldFetch) return;
        if (filter === "Most Recent") {
            handleGetNeighborhoodNews(page);
        } else {
            isFetchingRef.current = true;
            dispatch(neighborhoodNewsFilter({ selectedFilter: getApiFilter(filter), page }))
                .unwrap()
                .then((response) => {
                    hasMoreRef.current = response.next !== null;
                    lastPageRef.current = page;
                })
                .catch((error) => {
                    console.error("Failed to fetch filtered news:", error);
                })
                .finally(() => {
                    isFetchingRef.current = false;
                });
        }
    }, [page, fetchKey, filter, dispatch, handleGetNeighborhoodNews]);

    useEffect(() => {
        if (filter !== "Most Recent" && page === 1 && !isFetchingRef.current && lastPageRef.current === 0) {
            isFetchingRef.current = true;
            dispatch(neighborhoodNewsFilter({ selectedFilter: getApiFilter(filter), page: 1 }))
                .unwrap()
                .then((response) => {
                    hasMoreRef.current = response.next !== null;
                    lastPageRef.current = 1;
                })
                .catch((error) => {
                    console.error("Failed to fetch filtered news:", error);
                })
                .finally(() => {
                    isFetchingRef.current = false;
                });
        }
    }, [filter, dispatch]);

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPage(1);
        setFetchKey((k) => k + 1);
        setDropdownOpen(false);
        isFetchingRef.current = false;
        hasMoreRef.current = true;
        lastPageRef.current = 0;
        dispatch({ type: "NeighborhoodNews/resetNews" });
    };

    const handleClickOutside = useCallback((event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setDropdownOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => { document.removeEventListener("mousedown", handleClickOutside); };
    }, [handleClickOutside]);

    useEffect(() => {
        if (reportNews) dispatch(getReportReasons());
    }, [reportNews, dispatch]);

    useEffect(() => {
        if (reportNews || blockUserId) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [reportNews, blockUserId]);

    const toggleMenu = () => setMenuOpen(!menuOpen);

    const handleRefresh = useCallback(() => {
        isFetchingRef.current = false;
        hasMoreRef.current = true;
        lastPageRef.current = 0;
        dispatch({ type: "NeighborhoodNews/resetNews" });
        setPage(1);
        setFetchKey((k) => k + 1);
    }, [dispatch]);

    const handleDeleteSuccess = handleRefresh;

    const handleBlockConfirm = async (id) => {
        try {
            const result = await dispatch(accountBlockUser(id));
            if (result.error) {
                toast.error("Failed to block user. Please try again.");
                console.error("Error blocking user:", result.error);
                return;
            } else {
                toast.success("User blocked successfully.");
            }
            setBlockUserId(null);
            setBlockUserInfo(null);
        } catch (error) {
            console.error("Error blocking user:", error);
            setBlockUserId(null);
            setBlockUserInfo(null);
        }
    };

    const handleCloseReportDrawer = () => {
        setReportNews(false);
        setReportNewsText("");
        setReportReason(null);
        setReportStep("reason");
        setReportNewsSuccess(false);
        setSelectedNewsId(null);
        setSelectedSubReason("");
        setReportError("");
    };

    const submitReportNews = async () => {
        if (!reportReason) return;
        const data = {
            reason: reportReason.code,
            ...(selectedSubReason && { sub_reason: selectedSubReason }),
            ...(reportNewsText.trim() && { user_comment: reportNewsText.trim() }),
        };
        try {
            setReportError("");
            const result = await dispatch(userReportNews({ newsId: selectedNewsId, data })).unwrap();
            if (result) {
                toast.success("News reported successfully.");
                setReportNewsSuccess(true);
            }
        } catch (error) {
            const msg = error?.message || error?.detail || "Failed to report news";
            setReportError(msg);
        }
    };

    const isReportSubmitDisabled = reportNewsLoading || !reportReason;

    const closePopup = () => {
        setIsPopupOpen(false);
        setSelectedPin(null);
    };

    return (
        <div className={`${menuOpen || reportNews ? "overflow-hidden" : ""} relative h-full w-full flex flex-col`}>
            <div className="sticky top-0 pb-[2px] z-50 shrink-0">
                <div className="relative flex items-center justify-center bg-[#BDE6FF] shadow-md rounded-b-xl px-2 py-1">
                    <div className="absolute left-2 top-1/2 transform -translate-y-1/2 flex items-center">
                        <ChevronLeft
                            onClick={() => navigate(-1)}
                            className="text-black dark:text-black cursor-pointer"
                            size={24}
                        />
                    </div>
                    <div className="flex flex-col items-center">
                        <p className="font-bold text-[18px] text-black dark:text-black">
                            Community Chat
                        </p>
                        <p className="flex items-center text-[10px] text-black dark:text-black">
                            <MapPin className="ml-[-3px]" size={14} />
                            <span className="ml-1 text-[10px] line-clamp-1">
                                {sessionLocation?.city || sessionLocation?.country
                                    ? `${sessionLocation?.city ?? ''}, ${sessionLocation?.country ?? ''}`.replace(/^,\s*|,\s*$/, '').trim()
                                    : User_profile?.session_location?.city || User_profile?.session_location?.country
                                        ? `${User_profile.session_location.city ?? ''}, ${User_profile.session_location.country ?? ''}`.replace(/^,\s*|,\s*$/, '').trim()
                                        : User_profile?.city || User_profile?.county
                                            ? `${User_profile?.city ?? ''}, ${User_profile?.county ?? ''}`.replace(/^,\s*|,\s*$/, '').trim()
                                            : 'No Location'}
                            </span>
                        </p>
                    </div>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-x-2">
                        <Menu
                            onClick={toggleMenu}
                            className="cursor-pointer text-black dark:text-black"
                            size={24}
                        />
                    </div>
                </div>

                {/* <div className="p-2 flex items-center justify-between gap-x-3 bg-white dark:bg-[#1E1D21] border-b border-gray-200 dark:border-gray-800/50">
                    <div
                        onClick={() => {
                            sessionStorage.setItem("neighborhoodtab", "news");
                            setActiveTab("news");
                        }}
                        className={`flex items-center justify-center
                            h-[30px] w-full cursor-pointer rounded-full transition-colors duration-300 font-bold border-2 border-gray-900 dark:border-gray-800/50
                            ${activeTab === "news" ? "bg-[#8a8686] dark:bg-[#333333] text-white dark:text-white" : "text-black dark:text-white"}
                        `}
                    >
                        <p className="text-[12px]">News</p>
                    </div>
                    <div
                        onClick={() => {
                            sessionStorage.setItem("neighborhoodtab", "chat");
                            setActiveTab("chat");
                        }}
                        className={`flex items-center justify-center
                            h-[30px] w-full cursor-pointer rounded-full transition-colors duration-300 font-bold border-2 border-gray-900 dark:border-gray-800/50
                            ${activeTab === "chat" ? "bg-[#8a8686] dark:bg-[#333333] text-white dark:text-white" : "text-black dark:text-white"}
                        `}
                    >
                        <p className="text-[12px]">Community Chat</p>
                    </div>
                </div> */}

                {activeTab === "news" && (
                    <div className="flex items-center justify-between px-2 bg-white dark:bg-[#1E1D21] pt-1">
                        <div
                            ref={dropdownRef}
                            className="flex items-center gap-x-[5px] border-b border-gray-400 dark:border-gray-800/50 w-fit relative"
                        >
                            <p className="text-[12px] text-black dark:text-white" onClick={() => setDropdownOpen(!dropdownOpen)}>{filter}</p>
                            <FaChevronDown
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className={`text-[10px] text-black dark:text-white cursor-pointer transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`}
                            />
                            {dropdownOpen && (
                                <div className="absolute w-[5.5rem] top-full left-0 mt-1 bg-white dark:bg-[#333333] border border-[#BDE6FF] overflow-hidden rounded shadow-lg">
                                    {FILTERS.filter(f => f !== filter).map((f) => (
                                        <p
                                            key={f}
                                            onClick={() => handleFilterChange(f)}
                                            className="text-[11px] text-black dark:text-white cursor-pointer px-2 py-1 hover:bg-gray-300 hover:text-black dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-800/50 last:border-b-0"
                                        >
                                            {f}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => navigate("/neighborhood/news/create")}
                            className="bg-[#BDE6FF] text-black text-[12px] font-semibold px-3 py-0.5 rounded-md cursor-pointer"
                        >
                            Create News
                        </button>
                    </div>
                )}
            </div>

            {activeTab === "pinboard" &&
                <div className="flex-1 min-h-0 relative">
                    <Pinboard
                        setSelectedPin={setSelectedPin}
                        selectedPin={selectedPin}
                        setIsPopupOpen={setIsPopupOpen}
                        isPopupOpen={isPopupOpen}
                    />
                </div>
            }
            {activeTab === "news" &&
                <div
                    ref={newsScrollRef}
                    className={`relative flex-1 min-h-0 bg-white dark:bg-[#1E1D21] ${reportNews || blockUserId ? 'overflow-hidden' : 'overflow-y-scroll'}`}
                >
                    <News
                        data={neighborhoodNews}
                        loading={newsLoading}
                        onRefresh={handleRefresh}
                        onDeleteSuccess={handleDeleteSuccess}
                        isOpen={blockUserId}
                        setIsOpen={setBlockUserId}
                        selectedNewsId={selectedNewsId}
                        setSelectedNewsId={setSelectedNewsId}
                        setReportNews={setReportNews}
                        setBlockUserInfo={setBlockUserInfo}
                    />
                    {isLoadMore && (
                        <div className="flex justify-center items-center py-4">
                            <BeatLoader size={10} color="#BDE6FF" loading={true} />
                        </div>
                    )}
                    {!newsLoading && !isLoadMore && neighborhoodNews.length > 0 && !hasMoreRef.current && (
                        <div className="flex justify-center items-center py-4">
                            <p className="text-[12px] text-gray-500 dark:text-gray-400">No more news to load</p>
                        </div>
                    )}
                </div>
            }
            {activeTab === "chat" &&
                <div className="flex-1 min-h-0 relative">
                    <Chat />
                </div>
            }

            {!!blockUserId && (
                <div className="absolute inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white h-auto py-4 dark:bg-[#1E1D21] shadow-lg rounded-lg w-[300px] flex flex-col items-center justify-center">
                        <p className="text-[24px] font-bold text-black dark:text-white text-center mb-2">
                            Block {blockUserInfo?.full_name || "User"}?
                        </p>
                        <p className="text-[12px] text-black px-4 dark:text-white text-center">
                            Are you sure you want to block {blockUserInfo?.full_name || "this user"}? They will not be able to send you messages,
                            find your profile, or see your posts and they will not be notified that you blocked them.
                        </p>
                        <div className="flex gap-2 mt-4">
                            <Button
                                onClick={() => {
                                    setBlockUserId(null);
                                    setBlockUserInfo(null);
                                }}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => handleBlockConfirm(blockUserId)}
                                size="sm"
                                className="bg-red-900 hover:bg-red-600 text-white"
                            >
                                Block
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {reportNews && (
                <Drawer
                    size={reportNewsSuccess ? 230 : 520}
                    placement="bottom"
                    open={!!reportNews}
                    onClose={handleCloseReportDrawer}
                    className="absolute w-full rounded-t-2xl bg-white dark:bg-gray-900 max-h-[90vh] shadow-2xl"
                    style={{ zIndex: 50 }}
                >
                    <div className={`relative flex flex-col ${reportNewsSuccess ? "h-auto" : "h-full max-h-[80vh]"}`}>
                        <div className="relative flex items-center justify-center px-4 pt-3 pb-2">
                            {reportStep === "detail" && !reportNewsSuccess && (
                                <IoIosArrowForward
                                    onClick={() => { setReportStep("reason"); setReportReason(null); setReportNewsText(""); setSelectedSubReason(""); setReportError(""); }}
                                    className="absolute left-4 top-4 cursor-pointer rotate-180 text-[20px] text-gray-800 dark:text-gray-100"
                                />
                            )}
                            {reportStep === "reason" && !reportNewsSuccess && (
                                <IoMdClose
                                    onClick={handleCloseReportDrawer}
                                    className="absolute right-4 top-3 cursor-pointer text-[20px] text-gray-800 dark:text-gray-100"
                                />
                            )}
                        </div>

                        {!reportNewsSuccess && reportStep === "reason" && (
                            <div className="flex h-full flex-col overflow-y-auto pb-6">
                                <div className="px-4 pt-1 pb-4 text-center space-y-1">
                                    <h2 className="text-[16px] font-semibold text-black dark:text-white">Report News</h2>
                                    <p className="text-[11px] text-gray-600 dark:text-gray-300">
                                        Reports are confidential. The person who posted won't know you reported it.
                                    </p>
                                </div>
                                <div className="border-t border-b border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800">
                                    {reportReasonsLoading ? (
                                        <div className="flex justify-center items-center py-8">
                                            <BeatLoader size={8} color={"#6b7280"} />
                                        </div>
                                    ) : (
                                        reportReasons.map((item) => (
                                            <button
                                                key={item.code}
                                                type="button"
                                                onClick={() => { setReportReason(item); setSelectedSubReason(""); setReportNewsText(""); setReportError(""); setReportStep("detail"); }}
                                                className="flex w-full items-center justify-between px-5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                                            >
                                                <span className="text-[12px] font-medium text-gray-900 dark:text-gray-100">{item.label}</span>
                                                <IoIosArrowForward className="text-[16px] text-gray-400" />
                                            </button>
                                        ))
                                    )}
                                </div>
                                <p className="mt-4 px-6 text-center text-[11px] leading-5 text-gray-500 dark:text-gray-400">
                                    Learn more about our <span className="text-blue-500 underline cursor-pointer">Community Guidelines</span>.
                                </p>
                            </div>
                        )}

                        {!reportNewsSuccess && reportStep === "detail" && reportReason && (
                            <div className="flex h-full flex-col overflow-y-auto pb-4">
                                <div className="px-4 pt-2 pb-3 text-center space-y-1">
                                    <h3 className="text-[14px] font-semibold text-black dark:text-white">{reportReason.label}</h3>
                                    <p className="text-[12px] text-gray-600 dark:text-gray-300">Tell us more so we can review this news.</p>
                                </div>
                                <div className="flex flex-col gap-3 px-4">
                                    {reportReason.sub_reasons?.length > 0 ? (
                                        <div className="overflow-hidden rounded-xl border border-gray-500 dark:border-gray-800 divide-y divide-gray-500 dark:divide-gray-800 bg-white dark:bg-gray-900">
                                            {reportReason.sub_reasons.map((sub) => (
                                                <button
                                                    key={sub.code}
                                                    type="button"
                                                    onClick={() => setSelectedSubReason(sub.code)}
                                                    className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                                                >
                                                    <span className="text-[12px] text-gray-900 dark:text-gray-100">{sub.label}</span>
                                                    <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${selectedSubReason === sub.code ? "border-gray-900 dark:border-gray-100" : "border-gray-500 dark:border-gray-700"}`}>
                                                        <span className={`h-2 w-2 rounded-full ${selectedSubReason === sub.code ? "bg-gray-900 dark:bg-gray-100" : "bg-transparent"}`} />
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 p-3 text-[12px] text-gray-600 dark:bg-gray-800 dark:text-gray-200">
                                            No additional categories for this reason. Add optional context below.
                                        </div>
                                    )}
                                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3">
                                        <textarea
                                            value={reportNewsText}
                                            maxLength={250}
                                            placeholder="Share anything that might help us understand the issue (optional)"
                                            onChange={(e) => setReportNewsText(e.target.value)}
                                            className="h-[90px] w-full resize-none bg-transparent text-[13px] text-gray-900 placeholder-gray-500 outline-none dark:text-gray-100 dark:placeholder-gray-400"
                                        />
                                        <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                                            {reportError && <span className="text-red-600">{reportError}</span>}
                                            <span>{reportNewsText.length}/250</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-auto flex gap-3 px-4 pt-4 pb-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseReportDrawer}
                                        className="flex-1 rounded-xl border border-gray-300 bg-white py-2 text-[14px] font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isReportSubmitDisabled}
                                        onClick={submitReportNews}
                                        className={`flex flex-1 items-center justify-center rounded-xl py-2 text-[14px] font-semibold transition ${isReportSubmitDisabled ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-800 dark:text-gray-500" : "bg-red-600 text-white hover:bg-red-700"}`}
                                    >
                                        {reportNewsLoading ? <BeatLoader size={10} color={"#fff"} /> : "Submit report"}
                                    </button>
                                </div>
                                <p className="px-6 pb-2 text-center text-[11px] text-gray-500 dark:text-gray-400">
                                    Reports are confidential. The person who posted won't know you reported it.
                                </p>
                            </div>
                        )}

                        {reportNewsSuccess && (
                            <div className="flex flex-col items-center justify-center gap-4 px-6 pb-6 pt-2 text-center">
                                <p className="text-[20px] font-semibold text-gray-900 dark:text-white">Thanks for letting us know</p>
                                <p className="text-[11px] text-gray-700 dark:text-gray-300">
                                    We'll review this news and take action if it violates our guidelines.
                                    You may not hear back, but your report helps keep the community safe.
                                </p>
                                <button
                                    onClick={handleCloseReportDrawer}
                                    className="w-full bg-gray-800 text-white rounded-xl py-2 text-[14px] font-semibold"
                                >
                                    Done
                                </button>
                                <p className="px-6 text-center text-[11px] leading-5 text-gray-500 dark:text-gray-400">
                                    Learn more about our <span className="text-blue-500 underline cursor-pointer">Community Guidelines</span>.
                                </p>
                            </div>
                        )}
                    </div>
                </Drawer>
            )}

            {menuOpen &&
                <div className="absolute inset-0 overflow-y-hidden z-50 flex">
                    <SliderMenu
                        toggleMenu={toggleMenu}
                        onClose={() => setMenuOpen(false)}
                    />
                </div>
            }

            {isPopupOpen && (
                <PinboardPopup
                    pinPost={selectedPin}
                    onClose={closePopup}
                    onUpdate={(updatedPost) => { setSelectedPin(updatedPost); }}
                />
            )}

            {showLocationPermissionDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="w-full max-w-sm bg-white dark:bg-[#1E1D21] rounded-2xl px-6 pt-6 pb-10">
                        {browserPermission === "denied" ? (
                            <>
                                <div className="flex justify-center mb-3">
                                    <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                                        <MdLocationOn className="text-[28px] text-red-500" />
                                    </div>
                                </div>
                                <p className="text-center text-[15px] font-bold text-black dark:text-white mb-1">
                                    Location Access Blocked
                                </p>
                                <p className="text-center text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                                    Location is blocked by your browser. Follow these steps to enable it, then click "Detect Location":
                                </p>
                                <div className="bg-gray-50 dark:bg-[#2a2a2e] rounded-xl p-3 mb-3 space-y-2">
                                    {BROWSER_STEPS[getBrowser()].map((step, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <span className="min-w-[20px] h-5 rounded-full bg-[#BDE6FF] text-black text-[10px] font-bold flex items-center justify-center mt-0.5">
                                                {i + 1}
                                            </span>
                                            <p className="text-[11px] text-gray-700 dark:text-gray-300">{step}</p>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 mb-3">
                                    Once enabled, click "Detect Location" below.
                                </p>
                                {detectBlockedError && (
                                    <p className="text-[11px] text-red-500 text-center mb-3">
                                        Location still blocked. Complete the steps above, then try again.
                                    </p>
                                )}
                                <button
                                    onClick={handleDetectWhenBlocked}
                                    disabled={isDetecting}
                                    className="w-full h-11 mb-3 rounded-xl bg-[#BDE6FF] text-black text-[14px] font-semibold disabled:opacity-60"
                                >
                                    {isDetecting ? "Detecting..." : "Detect Location"}
                                </button>
                                <button
                                    onClick={() => { setShowLocationPermissionDialog(false); setDetectBlockedError(false); }}
                                    className="w-full h-11 rounded-xl border border-gray-300 dark:border-gray-600 text-black dark:text-white text-[14px] font-semibold"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="flex justify-center mb-3">
                                    <div className="w-14 h-14 rounded-full bg-[#BDE6FF] flex items-center justify-center">
                                        <MdLocationOn className="text-[28px] text-black" />
                                    </div>
                                </div>
                                <p className="text-center text-[15px] font-bold text-black dark:text-white mb-1">
                                    Allow "Chaa Chingg" to access your location?
                                </p>
                                <p className="text-center text-[11px] text-gray-500 dark:text-gray-400 mb-6">
                                    Location access is required to show your neighborhood content.
                                </p>
                                <button
                                    onClick={() => handleLocationPermissionChoice("while_using")}
                                    className="w-full h-11 mb-3 rounded-xl bg-[#BDE6FF] text-black text-[14px] font-semibold"
                                >
                                    Allow While Using App
                                </button>
                                <button
                                    onClick={() => handleLocationPermissionChoice("once")}
                                    className="w-full h-11 mb-3 rounded-xl bg-[#BDE6FF] text-black text-[14px] font-semibold"
                                >
                                    Allow Once
                                </button>
                                <button
                                    onClick={() => handleLocationPermissionChoice("deny")}
                                    className="w-full h-11 rounded-xl border border-gray-300 dark:border-gray-600 text-black dark:text-white text-[14px] font-semibold"
                                >
                                    Don't Allow
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Neighborhood;
